import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Wind, Battery, Volume2, Feather, Star, ChevronDown, ShieldCheck, Truck, RotateCcw } from "lucide-react";
import { PRICE, COMPARE_PRICE, DISCOUNT_PERCENT, REVIEWS } from "@/lib/product-data";

/* ── Glass Layers ─────────────────────── */
const glassDeep    = "bg-white/[0.015] backdrop-blur-3xl border border-white/[0.04]";
const glassMedium  = "bg-white/[0.035] backdrop-blur-2xl border border-white/[0.07]";
const glassSurface = "bg-white/[0.06] backdrop-blur-xl border border-white/[0.1]";
const glassBright  = "bg-white/[0.08] backdrop-blur-lg border border-white/[0.14]";

function MiniStars({ count }) {
  return (
    <span className="inline-flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star key={i} size={11} fill={i < count ? "#22D3EE" : "transparent"} className={i < count ? "text-cyan-400" : "text-white/10"} />
      ))}
    </span>
  );
}

/* ── Floating Glass Orb ──────────────── */
function GlassOrb({ size, top, left, delay, color, blur, morphClass }) {
  return (
    <div
      className={`absolute rounded-full pointer-events-none ${morphClass}`}
      style={{
        width: size, height: size, top, left,
        background: color,
        filter: `blur(${blur})`,
        animationDelay: delay,
        opacity: 0.6,
      }}
    />
  );
}

export default function HomePage() {
  const navigate = useNavigate();

  const features = [
    { Icon: Wind, title: "360° Bladeless Airflow", desc: "48 micro-outlets deliver smooth, silent cooling without any exposed blades. Feel the air, not the source.", span: "md:col-span-7", accent: "from-cyan-400/20 to-cyan-400/0" },
    { Icon: Battery, title: "1500mAh Battery", desc: "USB-C fast charge. Up to 6 hours of runtime on a single charge.", span: "md:col-span-5", accent: "from-blue-400/20 to-blue-400/0" },
    { Icon: Volume2, title: "Whisper Quiet", desc: "Under 25dB on low settings — quieter than a library whisper.", span: "md:col-span-5", accent: "from-teal-400/20 to-teal-400/0" },
    { Icon: Feather, title: "Ultra-Light 400g", desc: "So light you'll forget it's there. Flexible ergonomic silicone band.", span: "md:col-span-7", accent: "from-purple-400/15 to-purple-400/0" },
  ];

  const stats = [
    { value: "50K+", label: "Fans Sold" },
    { value: "4.8", label: "Star Rating" },
    { value: "2,341", label: "5-Star Reviews" },
    { value: "30-Day", label: "Guarantee" },
  ];

  return (
    <div className="bg-[#030810] min-h-screen text-white relative overflow-hidden font-body">

      {/* ══════ LIQUID BACKGROUND LAYER ══════ */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {/* Primary morphing blobs */}
        <GlassOrb size="700px" top="-15%" left="5%" delay="0s" color="radial-gradient(circle, rgba(34,211,238,0.18) 0%, rgba(34,211,238,0) 70%)" blur="0px" morphClass="animate-morph-1" />
        <GlassOrb size="550px" top="40%" left="60%" delay="-6s" color="radial-gradient(circle, rgba(59,130,246,0.12) 0%, rgba(59,130,246,0) 70%)" blur="0px" morphClass="animate-morph-2" />
        <GlassOrb size="480px" top="70%" left="15%" delay="-12s" color="radial-gradient(circle, rgba(139,92,246,0.08) 0%, rgba(139,92,246,0) 70%)" blur="0px" morphClass="animate-morph-3" />
        <GlassOrb size="350px" top="20%" left="75%" delay="-4s" color="radial-gradient(circle, rgba(34,211,238,0.1) 0%, rgba(34,211,238,0) 70%)" blur="0px" morphClass="animate-morph-1" />
        <GlassOrb size="400px" top="-5%" left="50%" delay="-9s" color="radial-gradient(circle, rgba(20,184,166,0.1) 0%, rgba(20,184,166,0) 70%)" blur="0px" morphClass="animate-morph-3" />
        <GlassOrb size="300px" top="55%" left="-5%" delay="-15s" color="radial-gradient(circle, rgba(56,189,248,0.1) 0%, rgba(56,189,248,0) 70%)" blur="0px" morphClass="animate-morph-2" />

        {/* Deep ambient glow */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] bg-cyan-500/[0.04] rounded-full blur-[200px]" />
      </div>

      {/* ══════ GLASS HEADER ══════ */}
      <header data-testid="home-header" className="fixed top-0 w-full z-50">
        <div className="mx-4 mt-3">
          <div className={`${glassMedium} rounded-2xl px-6 py-3.5 flex justify-between items-center`}>
            <span className="font-heading text-xl font-bold tracking-tighter">
              breeze<span className="text-cyan-400">°</span>
            </span>
            <button
              data-testid="header-shop-btn"
              onClick={() => navigate("/product")}
              className={`${glassBright} rounded-full px-5 py-2 text-xs font-bold tracking-wider uppercase text-cyan-300 hover:bg-white/[0.12] hover:text-cyan-200 transition-all duration-300`}
            >
              Shop Now
            </button>
          </div>
        </div>
      </header>

      {/* ══════ HERO ══════ */}
      <section data-testid="home-hero" className="min-h-screen flex items-center justify-center relative pt-28 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center relative z-10">

          {/* Glass pill overline */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="inline-block mb-8">
            <span className={`${glassBright} rounded-full px-6 py-2.5 text-[11px] font-bold tracking-[0.3em] uppercase text-cyan-300 inline-flex items-center gap-2`}>
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-glow-breathe" />
              Introducing breeze°
            </span>
          </motion.div>

          {/* Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.7 }}
            className="font-heading text-5xl sm:text-7xl lg:text-[5.5rem] xl:text-[6.5rem] font-bold tracking-[-0.04em] leading-[0.85] mb-8"
          >
            <span className="block">Feel the</span>
            <span className="block bg-gradient-to-r from-cyan-300 via-cyan-400 to-blue-400 bg-clip-text text-transparent drop-shadow-[0_0_40px_rgba(34,211,238,0.3)]">
              Invisible Breeze
            </span>
          </motion.h1>

          {/* Description */}
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="text-base md:text-lg text-white/40 max-w-lg mx-auto mb-12 leading-relaxed">
            360° bladeless cooling that wraps around your neck. Zero noise. Zero hassle. Pure freedom.
          </motion.p>

          {/* ── GLASS CTA BUTTON ── */}
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.35 }}>
            <button
              data-testid="buy-now-cta"
              onClick={() => navigate("/product")}
              className="group relative inline-flex items-center justify-center gap-3 px-12 py-5 rounded-full text-lg font-bold overflow-hidden transition-all duration-500 hover:scale-105 active:scale-100"
            >
              {/* Glass background layers */}
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/25 to-blue-500/25 backdrop-blur-2xl rounded-full" />
              <div className="absolute inset-0 border border-white/20 rounded-full" />
              <div className="absolute inset-[1px] border border-white/[0.06] rounded-full" />

              {/* Glow behind */}
              <div className="absolute inset-0 rounded-full shadow-[0_0_60px_rgba(34,211,238,0.25),inset_0_1px_1px_rgba(255,255,255,0.15)] group-hover:shadow-[0_0_80px_rgba(34,211,238,0.45),inset_0_1px_1px_rgba(255,255,255,0.2)] transition-shadow duration-500" />

              {/* Shimmer sweep */}
              <div className="absolute top-0 left-0 w-[40%] h-full bg-gradient-to-r from-transparent via-white/[0.12] to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-[shimmer-sweep_1.5s_ease-in-out]" />

              {/* Glass reflection line at top */}
              <div className="absolute top-0 left-[15%] right-[15%] h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />

              {/* Text */}
              <span className="relative z-10 text-white">Get Yours — ${PRICE.toFixed(2)}</span>
              <ArrowRight size={20} className="relative z-10 text-cyan-300 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>

          {/* Price comparison */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-5 text-sm text-white/25">
            <span className="line-through">${COMPARE_PRICE.toFixed(2)}</span>
            <span className="mx-2 text-white/10">·</span>
            <span className="text-cyan-400/60">Save {DISCOUNT_PERCENT}%</span>
          </motion.div>

          {/* ── 3D PRODUCT VIDEO SHOWCASE ── */}
          <motion.div
            initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 1 }}
            className="mt-20 relative max-w-lg mx-auto"
          >
            {/* Glow underneath */}
            <div className="absolute -inset-8 rounded-[3rem] bg-white/[0.04] blur-[60px] animate-glow-breathe" />
            {/* Outer glass frame */}
            <div className="absolute -inset-3 rounded-[2rem] bg-white/[0.02] backdrop-blur-md border border-white/[0.04]" />
            {/* Inner glass frame */}
            <div className="absolute -inset-1 rounded-[1.75rem] bg-white/[0.01] border border-white/[0.03]" />

            {/* Video */}
            <div className="relative rounded-2xl overflow-hidden border border-white/[0.08]" data-testid="hero-product-video">
              <video
                autoPlay
                muted
                loop
                playsInline
                className="w-full object-cover"
                poster=""
              >
                <source src="/neckfan-hero.mp4" type="video/mp4" />
              </video>
              {/* Bottom fade into dark page */}
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#030810] to-transparent pointer-events-none" />
              {/* Top reflection */}
              <div className="absolute top-0 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-white/20 to-transparent z-10" />
            </div>

            {/* Floating glass orbs */}
            <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-cyan-400/[0.05] backdrop-blur-lg border border-white/[0.05] animate-float-medium" />
            <div className="absolute -bottom-3 -left-6 w-12 h-12 rounded-full bg-blue-400/[0.04] backdrop-blur-md border border-white/[0.04] animate-float-slow" style={{ animationDelay: "-3s" }} />
          </motion.div>

          {/* Scroll indicator */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }} className="mt-16 flex flex-col items-center gap-2">
            <span className="text-[10px] tracking-[0.3em] uppercase text-white/20">Scroll</span>
            <div className={`${glassDeep} rounded-full p-2`}>
              <ChevronDown size={14} className="text-white/30 animate-bounce" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════ FEATURES BENTO ══════ */}
      <section data-testid="home-features" className="py-28 md:py-36 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-20">
            <span className={`${glassBright} rounded-full px-5 py-2 text-[10px] font-bold tracking-[0.25em] uppercase text-cyan-300/80 inline-block mb-5`}>
              Engineered for Cool
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">Built Different</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className={`${f.span} group cursor-default relative`}
              >
                {/* Outer glass shell */}
                <div className={`${glassMedium} rounded-2xl p-[1px] h-full`}>
                  {/* Inner glass surface */}
                  <div className="relative bg-white/[0.02] rounded-[15px] p-8 h-full overflow-hidden">
                    {/* Accent gradient glow */}
                    <div className={`absolute top-0 left-0 w-full h-full bg-gradient-to-br ${f.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                    {/* Top reflection */}
                    <div className="absolute top-0 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

                    <div className="relative z-10">
                      <div className={`w-11 h-11 rounded-xl ${glassSurface} flex items-center justify-center mb-5 group-hover:shadow-[0_0_20px_rgba(34,211,238,0.15)] transition-shadow duration-300`}>
                        <f.Icon size={20} className="text-cyan-400" />
                      </div>
                      <h3 className="font-heading text-lg font-semibold mb-2 text-white/90 group-hover:text-white transition-colors">{f.title}</h3>
                      <p className="text-sm text-white/30 leading-relaxed group-hover:text-white/45 transition-colors">{f.desc}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════ SOCIAL PROOF ══════ */}
      <section data-testid="home-social-proof" className="py-20 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Multi-layer glass container */}
          <div className="relative">
            <div className="absolute -inset-3 rounded-[2rem] bg-white/[0.01] backdrop-blur-md border border-white/[0.03]" />
            <div className={`${glassMedium} rounded-3xl p-10 md:p-14 relative overflow-hidden`}>
              {/* Inner glow */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] bg-cyan-400/[0.04] rounded-full blur-[80px]" />
              {/* Reflection */}
              <div className="absolute top-0 left-[15%] right-[15%] h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center relative z-10">
                {stats.map((s, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                    <div className="font-heading text-3xl sm:text-4xl font-bold bg-gradient-to-b from-cyan-300 to-cyan-500 bg-clip-text text-transparent">{s.value}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/25 mt-2 font-semibold">{s.label}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════ REVIEWS ══════ */}
      <section data-testid="home-reviews" className="pb-28 md:pb-36 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <span className={`${glassBright} rounded-full px-5 py-2 text-[10px] font-bold tracking-[0.25em] uppercase text-cyan-300/80 inline-block mb-5`}>
              Loved By Thousands
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">What People Say</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {REVIEWS.slice(0, 3).map((r, i) => {
              const opacities = ["bg-white/[0.04]", "bg-white/[0.03]", "bg-white/[0.025]"];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className="group relative"
                  data-testid={`home-review-${i}`}
                >
                  <div className={`${opacities[i]} backdrop-blur-2xl border border-white/[0.06] rounded-2xl p-6 relative overflow-hidden hover:bg-white/[0.06] hover:border-white/[0.1] hover:-translate-y-1 transition-all duration-400`}>
                    {/* Reflection */}
                    <div className="absolute top-0 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />
                    {/* Hover glow */}
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[60%] h-[40%] bg-cyan-400/[0.03] rounded-full blur-[40px] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                    <div className="relative z-10">
                      <MiniStars count={r.rating} />
                      <p className="text-sm text-white/35 leading-relaxed mt-3 mb-4 group-hover:text-white/50 transition-colors">"{r.text}"</p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-white/50">{r.name}</span>
                        {r.verified && <span className="text-cyan-400/60 text-[10px] font-bold tracking-wider uppercase">Verified</span>}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══════ FINAL CTA ══════ */}
      <section data-testid="home-final-cta" className="pb-32 relative text-center">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          {/* Morphing glass backdrop */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] bg-cyan-400/[0.04] rounded-full blur-[100px] animate-morph-2" />

          <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold tracking-[-0.04em] mb-6 relative z-10">
            Stay Cool<span className="text-cyan-400">.</span><br />
            Stay Free<span className="text-cyan-400">.</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-white/30 max-w-md mx-auto mb-12 relative z-10">
            Join 50,000+ people who ditched the handheld fan.
          </motion.p>

          <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="relative z-10">
            <button
              data-testid="final-cta-home"
              onClick={() => navigate("/product")}
              className="group relative inline-flex items-center justify-center gap-3 px-12 py-5 rounded-full text-lg font-bold overflow-hidden transition-all duration-500 hover:scale-105"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/20 to-blue-500/20 backdrop-blur-2xl rounded-full" />
              <div className="absolute inset-0 border border-white/15 rounded-full" />
              <div className="absolute inset-0 rounded-full shadow-[0_0_50px_rgba(34,211,238,0.2),inset_0_1px_1px_rgba(255,255,255,0.1)] group-hover:shadow-[0_0_70px_rgba(34,211,238,0.4),inset_0_1px_1px_rgba(255,255,255,0.15)] transition-shadow duration-500" />
              <div className="absolute top-0 left-[15%] right-[15%] h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />
              <span className="relative z-10 text-white">Shop Now</span>
              <ArrowRight size={20} className="relative z-10 text-cyan-300 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>

          {/* Trust pills */}
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="flex flex-wrap justify-center gap-3 mt-10 relative z-10">
            {[
              { Icon: Truck, text: "Free Shipping" },
              { Icon: RotateCcw, text: "30-Day Returns" },
              { Icon: ShieldCheck, text: "Secure Checkout" },
            ].map((b, i) => (
              <span key={i} className={`${glassDeep} rounded-full px-4 py-2 text-[10px] font-semibold text-white/30 inline-flex items-center gap-2 tracking-wider uppercase`}>
                <b.Icon size={12} className="text-cyan-400/50" /> {b.text}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══════ FOOTER ══════ */}
      <footer data-testid="home-footer" className="relative">
        <div className="mx-4 mb-4">
          <div className={`${glassDeep} rounded-2xl px-8 py-8 flex flex-col sm:flex-row justify-between items-center gap-4`}>
            <span className="font-heading text-lg font-bold tracking-tighter">breeze<span className="text-cyan-400">°</span></span>
            <span className="text-[10px] text-white/20 tracking-widest uppercase">&copy; 2026 breeze° — All rights reserved</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
